
import { useState, useEffect, useRef } from 'react';
import { CalendarRange, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';

import { FilterableTableHead } from './FilterableTableHead';
import { CampaignTableRow } from './CampaignTableRow';
import { BulkActionsBar } from './BulkActionsBar';
import { ModernPagination } from '@/components/ui/modern-pagination';
import { CampaignStatus } from '@/types/states';
import { ErrorHandler } from '@/services/ErrorHandler';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface DashboardReferent {
  id: string;
  name: string;
  email: string;
  status: string;
  relation_type: string | null;
  invitation_sent_at: string | null;
  last_email_sent_at: string | null;
  email_opens_count: number | null;
  email_clicks_count: number | null;
  reminders_sent_count: number | null;
}

interface DashboardCampaign {
  id: string;
  title: string;
  description: string;
  candidate_name?: string;
  candidate_email?: string;
  target_position?: string;
  status: CampaignStatus | string; // Support both unified enum and legacy strings
  organization_id: string;
  created_at: string;
  updated_at: string;
  evaluation_criteria: string[];
  unique_link?: string | null;
  ai_extracted_data?: any;
  last_activity?: string | null;
  referents_count: number;
  completed_referents: number;
  referents?: DashboardReferent[];
}

interface CampaignsTableProps {
  campaigns: DashboardCampaign[] | undefined;
  loading?: boolean;
  fetched?: boolean; // ✅ Ajouté pour gate l'empty state
  urlFilter?: string | null;
  onResetFilter?: () => void;
  refetchCampaigns: () => void; // ✅ PHASE 1A: Prop pour passer aux rows
}

export const CampaignsTable = ({ campaigns, loading = false, fetched = false, urlFilter, onResetFilter, refetchCampaigns }: CampaignsTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [periodFilter, setPeriodFilter] = useState<string>('tous');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const periodDropdownRef = useRef<HTMLDivElement>(null);

  const [candidateFilter, setCandidateFilter] = useState<string>('tous');
  const [statusFilter, setStatusFilter] = useState<string>('tous');
  const [positionFilter, setPositionFilter] = useState<string>('tous');
  const [dateSort, setDateSort] = useState<string>('none');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [specialFilter, setSpecialFilter] = useState<string | null>(null);
  const { toast } = useToast();

  // Appliquer le filtre URL au chargement
  useEffect(() => {
    if (urlFilter) {
      setSpecialFilter(urlFilter);
    } else {
      setSpecialFilter(null);
    }
  }, [urlFilter]);

  // Fermer les dropdowns quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target as Node)) {
        setPeriodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Créer les listes uniques pour les filtres
  const uniqueCandidates = Array.from(new Set(
    campaigns?.map(c => c.candidate_name)
      .filter(Boolean)
      .filter(name => typeof name === 'string' && name.trim() !== '')
    || []
  ));

  const uniquePositions = Array.from(new Set(
    campaigns?.map(c => c.target_position)
      .filter(Boolean)
      .filter(pos => typeof pos === 'string' && pos.trim() !== '')
    || []
  ));

  // État pour stocker les IDs de campagnes avec fraude
  const [fraudCampaignIds, setFraudCampaignIds] = useState<Set<string>>(new Set());

  // Charger les campagnes avec fraude si le filtre est actif
  useEffect(() => {
    const loadFraudCampaigns = async () => {
      if (specialFilter === 'fraud') {
        // Support ancien et nouveau système (coexistence)
        const { data } = await supabase
          .from('fraud_alert_tracking')
          .select('campaign_id')
          .in('alert_type', [
            'referent_email_validation_failed', // Ancien système
            'referent_evaluation_fraud',        // Ancien système
            'referent_unified_fraud_analysis'   // Nouveau système unifié
          ])
          .not('campaign_id', 'is', null);
        
        if (data) {
          setFraudCampaignIds(new Set((data as any[]).map((item: any) => item.campaign_id)));
        }
      }
    };

    loadFraudCampaigns();
  }, [specialFilter]);

  // Filtrer les campagnes avec gestion d'erreurs unifiée
  let filteredCampaigns: DashboardCampaign[] | undefined;
  
  try {
    filteredCampaigns = campaigns?.filter(campaign => {
      // Filtre spécial depuis l'URL
      if (specialFilter === 'overdue') {
        // Campagnes hors délais
        const now = Date.now();
        const createdAt = new Date(campaign.created_at).getTime();
        const durationDays = campaign.ai_extracted_data?.campaign_duration === '5_days' ? 5 : 3;
        const deadline = createdAt + (durationDays * 24 * 60 * 60 * 1000);
        const isOverdue = now > deadline;
        const isActive = !['Terminé', 'Annulée', 'Refusé'].includes(campaign.status);
        
        if (!isOverdue || !isActive) return false;
      } else if (specialFilter === 'fraud') {
        // Campagnes avec suspicion de fraude
        if (!fraudCampaignIds.has(campaign.id)) return false;
      }

      const matchesSearch =
        campaign.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.candidate_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.target_position?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCandidate = candidateFilter === 'tous' || campaign.candidate_name === candidateFilter;
      const matchesStatus = statusFilter === 'tous' || campaign.status === statusFilter;
      const matchesPosition = positionFilter === 'tous' || campaign.target_position === positionFilter;
      
      // Filtre de période
      let matchesPeriod = true;
      if (periodFilter !== 'tous') {
        const campaignDate = new Date(campaign.created_at);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - campaignDate.getTime()) / (1000 * 60 * 60 * 24));

        switch (periodFilter) {
          case '7-jours':
            matchesPeriod = daysDiff <= 7;
            break;
          case '30-jours':
            matchesPeriod = daysDiff <= 30;
            break;
          case '90-jours':
            matchesPeriod = daysDiff <= 90;
            break;
        }
      }
      
      return matchesSearch && matchesCandidate && matchesStatus && matchesPosition && matchesPeriod;
    });
  } catch (error) {
    const userError = ErrorHandler.handle(error as Error, {
      component: 'CampaignsTable',
      function: 'filterCampaigns'
    });
    
    toast({
      title: "Erreur de filtrage",
      description: userError.message,
      variant: "destructive"
    });
    
    filteredCampaigns = campaigns; // Fallback to unfiltered data
  }

  // Trier par date si nécessaire
  if (dateSort !== 'none' && filteredCampaigns) {
    filteredCampaigns = [...filteredCampaigns].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateSort === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }

  // Calculate pagination
  const totalItems = filteredCampaigns?.length || 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCampaigns = filteredCampaigns?.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filters change
  const handleFiltersChange = () => {
    setCurrentPage(1);
  };

  // Update handlers to trigger filter change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    handleFiltersChange();
  };

  const handleCandidateChange = (value: string) => {
    setCandidateFilter(value);
    handleFiltersChange();
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    handleFiltersChange();
  };

  const handlePositionChange = (value: string) => {
    setPositionFilter(value);
    handleFiltersChange();
  };

  const handleDateSortChange = (value: string) => {
    setDateSort(value);
    handleFiltersChange();
  };

  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    handleFiltersChange();
  };

  // Options pour les filtres
  const candidateOptions = [
    { value: 'tous', label: 'Tous les candidats' },
    ...uniqueCandidates.map((candidate) => ({ value: candidate, label: candidate }))
  ];

  const positionOptions = [
    { value: 'tous', label: 'Tous les postes' },
    ...uniquePositions.map((position) => ({ value: position, label: position }))
  ];

  // Fonctions de gestion de la sélection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = paginatedCampaigns?.map(campaign => campaign.id) || [];
      setSelectedCampaigns(allIds);
    } else {
      setSelectedCampaigns([]);
    }
  };

  const handleSelectCampaign = (campaignId: string, checked: boolean) => {
    if (checked) {
      setSelectedCampaigns(prev => [...prev, campaignId]);
    } else {
      setSelectedCampaigns(prev => prev.filter(id => id !== campaignId));
    }
  };

  const isAllSelected = (paginatedCampaigns?.length || 0) > 0 && selectedCampaigns.length === (paginatedCampaigns?.length || 0);
  const toolbarButtonClass =
    'inline-flex h-10 items-center gap-2 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3.5 text-[13px] font-medium text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]';
  const dropdownClass =
    'absolute top-full left-0 z-50 mt-2 min-w-max overflow-hidden rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.08)]';
  const dropdownItemClass =
    'flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2 text-[13px] text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] whitespace-nowrap';

  return (
    <div className="space-y-4">
      {selectedCampaigns.length > 0 && (
        <BulkActionsBar
          selectedCampaigns={selectedCampaigns}
          campaigns={filteredCampaigns || []}
          onClearSelection={() => setSelectedCampaigns([])}
          onRefresh={() => {
            // Ici on pourrait appeler une fonction de refresh passée en props
            // Pour l'instant on vide juste la sélection
          }}
        />
      )}
      <div className="rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex h-10 w-full items-center gap-2 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3 shadow-[0_6px_18px_rgba(15,23,42,0.04)] lg:max-w-[420px]">
            <Search className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
          <input
            type="text"
            placeholder="Recherche par candidat, email ou poste..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
              className="flex-1 bg-transparent text-[14px] text-[hsl(var(--app-text-strong))] outline-none placeholder:text-[hsl(var(--app-text-muted))]"
          />
          </div>

          <div className="flex flex-wrap items-center gap-2">
          <div className="relative" ref={statusDropdownRef}>
            <button
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                className={toolbarButtonClass}
            >
                <SlidersHorizontal className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
                <div className="whitespace-nowrap">
                {statusFilter === 'tous' ? 'Statut' : statusFilter}
              </div>
                <ChevronDownIcon />
            </button>
            
            {statusDropdownOpen && (
                <div className={dropdownClass}>
                <div 
                  onClick={() => { handleStatusChange('tous'); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === 'tous' ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === 'tous' ? 'text-[hsl(var(--app-accent))]' : ''}>
                    Tous les statuts
                  </div>
                  {statusFilter === 'tous' && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.BROUILLON); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.BROUILLON ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.BROUILLON ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.BROUILLON}
                  </div>
                  {statusFilter === CampaignStatus.BROUILLON && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.EN_ATTENTE_CANDIDAT); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.EN_ATTENTE_CANDIDAT ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.EN_ATTENTE_CANDIDAT ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.EN_ATTENTE_CANDIDAT}
                  </div>
                  {statusFilter === CampaignStatus.EN_ATTENTE_CANDIDAT && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.EN_ATTENTE_REFERENTS); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.EN_ATTENTE_REFERENTS ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.EN_ATTENTE_REFERENTS ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.EN_ATTENTE_REFERENTS}
                  </div>
                  {statusFilter === CampaignStatus.EN_ATTENTE_REFERENTS && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.TERMINE); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.TERMINE ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.TERMINE ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.TERMINE}
                  </div>
                  {statusFilter === CampaignStatus.TERMINE && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.ANNULEE); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.ANNULEE ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.ANNULEE ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.ANNULEE}
                  </div>
                  {statusFilter === CampaignStatus.ANNULEE && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handleStatusChange(CampaignStatus.REFUSE); setStatusDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${statusFilter === CampaignStatus.REFUSE ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={statusFilter === CampaignStatus.REFUSE ? 'text-[hsl(var(--app-accent))]' : ''}>
                    {CampaignStatus.REFUSE}
                  </div>
                  {statusFilter === CampaignStatus.REFUSE && (
                      <CheckIcon />
                  )}
                </div>
              </div>
            )}
          </div>

          {urlFilter && (
            <button
              onClick={onResetFilter}
                className="inline-flex h-10 items-center gap-2 rounded-[14px] border border-emerald-200/80 bg-emerald-50 px-3.5 text-[13px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
            >
                <RotateCcw className="h-4 w-4" />
                Réinitialiser
            </button>
          )}

          <div className="relative" ref={periodDropdownRef}>
            <button
              onClick={() => setPeriodDropdownOpen(!periodDropdownOpen)}
                className={toolbarButtonClass}
            >
                <CalendarRange className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
                <div className="whitespace-nowrap">
                {periodFilter === 'tous' ? 'Période' : 
                 periodFilter === '7-jours' ? '7 derniers jours' :
                 periodFilter === '30-jours' ? '30 derniers jours' :
                 periodFilter === '90-jours' ? '90 derniers jours' : 'Période'}
              </div>
                <ChevronDownIcon />
            </button>
            
            {periodDropdownOpen && (
                <div className={dropdownClass}>
                <div 
                  onClick={() => { handlePeriodChange('tous'); setPeriodDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${periodFilter === 'tous' ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={periodFilter === 'tous' ? 'text-[hsl(var(--app-accent))]' : ''}>
                    Toutes les périodes
                  </div>
                  {periodFilter === 'tous' && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handlePeriodChange('7-jours'); setPeriodDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${periodFilter === '7-jours' ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={periodFilter === '7-jours' ? 'text-[hsl(var(--app-accent))]' : ''}>
                    Les 7 derniers jours
                  </div>
                  {periodFilter === '7-jours' && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handlePeriodChange('30-jours'); setPeriodDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${periodFilter === '30-jours' ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={periodFilter === '30-jours' ? 'text-[hsl(var(--app-accent))]' : ''}>
                    Les 30 derniers jours
                  </div>
                  {periodFilter === '30-jours' && (
                      <CheckIcon />
                  )}
                </div>
                
                <div 
                  onClick={() => { handlePeriodChange('90-jours'); setPeriodDropdownOpen(false); }}
                    className={`${dropdownItemClass} ${periodFilter === '90-jours' ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
                >
                    <div className={periodFilter === '90-jours' ? 'text-[hsl(var(--app-accent))]' : ''}>
                    Les 90 derniers jours
                  </div>
                  {periodFilter === '90-jours' && (
                      <CheckIcon />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      <div className="overflow-hidden rounded-[20px] border border-[hsl(var(--app-border-subtle))] bg-white">
        <Table>
          <TableHeader className="bg-[hsl(var(--app-surface-muted))]/70">
            <TableRow className="border-b border-[hsl(var(--app-border-subtle))] hover:bg-transparent">
              <th className="w-12 px-4 py-3 text-left">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                    className="border-[hsl(var(--app-border-subtle))] data-[state=checked]:border-[hsl(var(--app-accent))] data-[state=checked]:bg-[hsl(var(--app-accent))]"
                />
              </th>
              <FilterableTableHead
                title="Candidat"
                filterType="select"
                value={candidateFilter}
                onValueChange={handleCandidateChange}
                options={candidateOptions}
                hasActiveFilter={candidateFilter !== 'tous'}
              />
              <FilterableTableHead
                title="Poste visé"
                filterType="select"
                value={positionFilter}
                onValueChange={handlePositionChange}
                options={positionOptions}
                hasActiveFilter={positionFilter !== 'tous'}
              />
              <FilterableTableHead
                title="Date de création"
                filterType="sort"
                value={dateSort}
                onValueChange={handleDateSortChange}
                hasActiveFilter={dateSort !== 'none'}
              />
              <th className="px-4 py-3 text-left">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                  Statut
                </div>
              </th>
              <FilterableTableHead
                title="Actions"
                filterType="none"
                className="text-left"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <div className="h-4 w-4 animate-pulse rounded bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-32 animate-pulse rounded bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-40 animate-pulse rounded bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 animate-pulse rounded bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 animate-pulse rounded-full bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <div className="h-8 w-8 animate-pulse rounded-xl bg-[hsl(var(--app-surface-muted))]" />
                      <div className="h-8 w-8 animate-pulse rounded-xl bg-[hsl(var(--app-surface-muted))]" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : fetched && totalItems === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-10 text-center text-[14px] text-[hsl(var(--app-text-muted))]"
                >
                  Aucune campagne trouvée
                </TableCell>
              </TableRow>
            ) : (
              paginatedCampaigns?.map((campaign) => (
                <CampaignTableRow 
                  key={campaign.id} 
                  campaign={campaign}
                  isSelected={selectedCampaigns.includes(campaign.id)}
                  onSelectChange={handleSelectCampaign}
                  refetchCampaigns={refetchCampaigns} // ✅ PHASE 1A: Passé en prop
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {totalItems > 0 && (
        <ModernPagination
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.0625 6.5625L4.6875 9.1875L9.9375 3.5625"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
